/**
 * Checkout protocol (Neel Slack DM 2026-10-19) — the "coliviq AI" reject
 * composer. No reason-chip / LLM-drafting pattern exists anywhere in the
 * repo (confirmed: the closest real mechanism, `generate-email-reply`'s
 * `adminInstruction` param, has never been wired to a caller). Rather than
 * being the first caller of a live LLM on a tenant-facing lease email, this
 * is a deterministic template per reason — predictable wording, no new edge
 * function. "Redraft" just recomposes from the current reason + context; the
 * context text is inserted as typed, not reworded.
 */

export type MoveOutRejectReason =
  | "room_relet"
  | "before_contract_end"
  | "notice_period_not_met"
  | "open_payments"
  | "other";

export const MOVE_OUT_REJECT_REASONS: {
  id: MoveOutRejectReason;
  label: { de: string; en: string };
}[] = [
  {
    id: "room_relet",
    label: { de: "Zimmer bereits neu vermietet", en: "Room already re-let" },
  },
  {
    id: "before_contract_end",
    label: { de: "Vor Vertragsende", en: "Before contract end" },
  },
  {
    id: "notice_period_not_met",
    label: {
      de: "Kündigungsfrist nicht eingehalten",
      en: "Notice period not met",
    },
  },
  {
    id: "open_payments",
    label: { de: "Offene Zahlungen", en: "Open payments" },
  },
  { id: "other", label: { de: "Sonstiges", en: "Other" } },
];

const REASON_CORE: Record<MoveOutRejectReason, { de: string; en: string }> = {
  room_relet: {
    de: "danke für deine Anfrage. Den {date} können wir leider nicht bestätigen: Dein Zimmer ist ab diesem Tag bereits neu vermietet, und wir brauchen für die Übergabe einen Tag Vorlauf.",
    en: "thanks for letting us know. We can't confirm {date} — your room is already re-let from that day, and we need a day's lead time for the handover.",
  },
  before_contract_end: {
    de: "danke für deine Anfrage. Der {date} liegt leider vor dem frühestmöglichen Auszugstermin laut deinem Mietvertrag.",
    en: "thanks for letting us know. {date} is before the earliest move-out date your contract allows.",
  },
  notice_period_not_met: {
    de: "danke für deine Anfrage. Für den {date} wurde die vertraglich vereinbarte Kündigungsfrist noch nicht eingehalten.",
    en: "thanks for letting us know. The notice period in your contract hasn't been met yet for {date}.",
  },
  open_payments: {
    de: "danke für deine Anfrage. Bevor wir deinen Auszug zum {date} bestätigen können, müssen offene Zahlungen auf deinem Konto geklärt werden.",
    en: "thanks for letting us know. Before we can confirm your move-out on {date}, there are open payments on your account we need to clear up first.",
  },
  other: {
    de: "danke für deine Anfrage. Den {date} können wir aktuell leider nicht bestätigen.",
    en: "thanks for letting us know. We can't confirm {date} right now.",
  },
};

export function moveOutRejectSubject(
  lang: "de" | "en",
  requestedDateLabel: string
): string {
  return lang === "de"
    ? `Dein Auszugswunsch zum ${requestedDateLabel}`
    : `About your requested check-out date on ${requestedDateLabel}`;
}

export function moveOutRejectTemplate(
  reason: MoveOutRejectReason,
  lang: "de" | "en",
  ctx: {
    tenantFirstName: string;
    requestedDateLabel: string;
    context?: string;
  }
): { subject: string; body: string } {
  const greeting =
    lang === "de"
      ? `Hallo ${ctx.tenantFirstName},`
      : `Hi ${ctx.tenantFirstName},`;
  const core = REASON_CORE[reason][lang].replace(
    /\{date\}/g,
    ctx.requestedDateLabel
  );
  const closing =
    lang === "de"
      ? "Viele Grüße\nDein coliviq-Team"
      : "Best,\nThe coliviq Team";
  const paragraphs = [greeting, "", core];
  if (ctx.context?.trim()) {
    paragraphs.push("", ctx.context.trim());
  }
  paragraphs.push("", closing);
  return {
    subject: moveOutRejectSubject(lang, ctx.requestedDateLabel),
    body: paragraphs.join("\n"),
  };
}
